import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import type { LanguageCode } from "@/api/orval/model";

import { i18n as configuredI18n, fallbackLng, languages } from "../i18n.config";
import { BCP47LangCode } from "../i18n.types";

/** Reads the configured UI language outside React, including route loaders. */
export function getCurrentLangCode(i18n = configuredI18n): LanguageCode {
  const currentLang = i18n.resolvedLanguage ?? fallbackLng;
  return currentLang.replace("-", "_") as LanguageCode;
}

/**
 * Hook that exposes the current language, available languages, and a function
 * to switch languages. Useful for language-picker components and for passing
 * the resolved locale to formatting utilities.
 *
 * @example
 * ```tsx
 * const { currentLang, changeLanguage, languages } = useI18n();
 * ```
 */
export function useI18n() {
  const { i18n } = useTranslation();

  const currentLang = (i18n.resolvedLanguage ?? fallbackLng) as BCP47LangCode;

  const changeLanguage = useCallback((lang: BCP47LangCode) => i18n.changeLanguage(lang), [i18n]);

  return {
    /** The BCP 47 language tag currently in effect. */
    currentLang,
    /**
     * `currentLang` as the backend `LanguageCode` enum (underscore form, e.g. `en_US`).
     * Use this when sending the UI language to an API — never hand-roll the mapping.
     * Mirrors `useI18n().currentLangCode` in AgelessAI.
     */
    currentLangCode: getCurrentLangCode(i18n),
    /** The default / fallback language. */
    defaultLang: fallbackLng,
    /** All supported languages. */
    languages,
    /** Switch the active language (persisted to localStorage / cookie). */
    changeLanguage,
  };
}
